import { ALGOLIA_INDEX } from '@commons/utils/constants';

/**
 * Type definition for the getAlgoliaRoute function options.
 */

export type GetSearchURLOptions = {
  /**
   * The filters to apply to the search query, as key-value pairs.
   *
   * The key should already have an Algolia routing friendly format.
   *
   * @example
   * getSearchURL('/search', { filters: { "[query]": "Hello world" } });
   * getSearchURL('/insights', { filters: { "[refinementList][topics][0]": "Civil unrest" } });
   */
  filters: Record<string, string>;

  /**
   * The Algolia index to use.
   *
   * The primary index is used by default.
   */
  index?: string;

  /**
   * An optional anchor to append to the URL.
   *
   * Set to `search-wrapper` by default as its the search layout's ID.
   */
  anchor?: string;
};

/**
 * Generate an Algolia search URL compatible with its internal routing.
 *
 * @param path - The path to the search page (i.e. /search).
 * @param options - The options to customize the Algolia search URL.
 *
 * @returns An Algolia search URL compatible with its internal routing.
 *
 * @example
 * getSearchURL('/search', { filters: { "[query]": 'test' } });
 * // => /search?index_name[query]=test#search-wrapper
 */

export const getSearchURL = (path: string, options: GetSearchURLOptions): string => {
  const { filters, index = ALGOLIA_INDEX, anchor = 'search-wrapper' } = options;

  // InstantSearch uses `%20` instead of `+` for encoded spaces, so we can't use the URLSearchParams API.
  // This ensures that the URL is not transformed by the InstantSearch routing after the page loads.
  const params = Object.entries(filters)
    .map(([key, value]) => {
      const encodedKey = encodeURIComponent(`${index}${key}`);
      const encodedValue = encodeURIComponent(value);
      return `${encodedKey}=${encodedValue}`;
    })
    .join('&');

  return `${path}?${params}${anchor && `#${anchor}`}`;
};
